import { useLazyQuery } from "@apollo/client";
import {
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Grid,
  TablePagination,
  InputBase,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { debounce } from "lodash";

import { MatchContext } from "../../context";
import useStyles from "./styles";
import {
  GET_MATCHING_REQUESTS,
  GET_USER_INPUT_DATA,
} from "../../queries/stockpiles.gql";
import {
  SET_DATA,
  SET_FORECASTED_STOCKPILE,
  SET_TABLE_INPUTS,
  SET_USER_INPUT_DATA,
} from "../../reduce/actionTypes";
import LoaderBar from "../common/loader-bar.component";
import { keysToUpperCase } from "../../utils/create-table-data";
import ErrorMessage from "../common/error-message.component";
import {
  APP_INSIGHTS_EVENTS,
  STOCKPILES_TABLE_FEATURES,
} from "../../constants";
import useTrackEvent from "../../utils/hooks/use-track-app-event";
import prepareFeaturesData from "../../utils/prepare-features-data";

const MatchingLoaderModal = (): JSX.Element => {
  const { state, dispatch } = useContext(MatchContext);
  const classes = useStyles();
  const trackEvent = useTrackEvent();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState<number | null>(null);
  const [searchData, setSearchData] = useState("");
  const [
    getMatchingResults,
    { data: matchingResults, loading: matchingResultsLoading, error },
  ] = useLazyQuery(GET_MATCHING_REQUESTS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setTotal(data.getMatchingRequests.total);
    },
  });
  const [getUserInputData] = useLazyQuery(GET_USER_INPUT_DATA, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      dispatch({
        type: SET_USER_INPUT_DATA,
        payload: data,
      });

      // just to trigger ZnPb/ZnFe/PbFe Ratio calculations
      dispatch({
        type: SET_TABLE_INPUTS,
        payload: keysToUpperCase(data.getUserInputData.tableInputs),
      });

      trackEvent(APP_INSIGHTS_EVENTS.CLICK_LOAD_STOCKPILE, {
        stockpileName: data.getUserInputData.stockpileName,
        startDate: data.getUserInputData.startDate,
        endDate: data.getUserInputData.endDate,
        featuresData: prepareFeaturesData(
          STOCKPILES_TABLE_FEATURES,
          keysToUpperCase(data.getUserInputData.tableInputs),
          keysToUpperCase(data.getUserInputData.tableWeights)
        ),
      });
    },
  });

  useEffect(() => {
    getMatchingResults({
      variables: {
        offset: page,
        limit: rowsPerPage,
      },
    });
  }, []);

  useEffect(() => {
    if (state.showRequestsModal) {
      getMatchingResults({
        variables: {
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          search: searchData,
        },
      });
    }
  }, [page, rowsPerPage, searchData, state.showRequestsModal]);

  const handleCloseModal = () => {
    dispatch({
      type: SET_DATA,
      payload: { showRequestsModal: false },
    });
  };

  const handleChangePage = (e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = e.target.value;

    if (value.length < 256) {
      // setSesarchData(value);
      debouncedHandler(value);
    }
  };

  const debouncedHandler = useCallback(
    debounce((val) => {
      setSearchData(val);
      setPage(0);
    }, 2000),
    []
  );

  const loadUserInputData = (requestId: string, bestMatch: any) => {
    getUserInputData({ variables: { requestId } });

    dispatch({
      type: SET_FORECASTED_STOCKPILE,
      payload: bestMatch,
    });

    dispatch({
      type: SET_DATA,
      payload: {
        requestId,
        showRequestsModal: false,
      },
    });
  };

  return (
    <div className={classes.matchingRequestContainer}>
      <Paper className={classes.main}>
        <Grid container className={classes.headContainer}>
          <Grid
            item
            container
            xs={11}
            justifyContent="flex-start"
            alignContent="center"
          >
            <InputBase
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
              }}
              type="text"
              placeholder="Search by `Input stockpile` / `Created by`"
              onChange={handleChange}
            />
          </Grid>
          <Grid
            item
            container
            xs={1}
            justifyContent="center"
            alignContent="center"
          >
            <Search className={classes.searchIcon} />
          </Grid>
        </Grid>
        <TableContainer className={classes.container}>
          <Table aria-label="matching-results-table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="row">
                  Input stockpile
                </TableCell>
                <TableCell component="th" scope="row">
                  Created at
                </TableCell>
                <TableCell component="th" scope="row">
                  Created by
                </TableCell>
                <TableCell component="th" scope="row">
                  Best match
                </TableCell>
                <TableCell component="th" scope="row"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchingResults?.getMatchingRequests?.requestsList &&
                matchingResults.getMatchingRequests?.requestsList.map(
                  (row, index) => {
                    return (
                      <TableRow key={index} id={row.requestId}>
                        <TableCell scope="row">{row.name || "---"}</TableCell>
                        <TableCell scope="row">
                          {row.createdAt
                            ? moment
                                .unix(row.createdAt)
                                .format("YYYY/MM/DD hh:mm")
                            : "---"}
                        </TableCell>
                        <TableCell scope="row">
                          {row.createdBy || "---"}
                        </TableCell>
                        <TableCell scope="row">
                          {row.bestMatch || "---"}
                        </TableCell>
                        <TableCell scope="row">
                          {row.successStatus ? (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => {
                                loadUserInputData(row.requestId, row.bestMatch);
                              }}
                              disabled={!row.successStatus}
                            >
                              Load
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              disabled={!row.successStatus}
                            >
                              Failed
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
            </TableBody>
          </Table>
          {matchingResultsLoading && (
            <div className={classes.loader}>
              <LoaderBar />
            </div>
          )}
          {error && <ErrorMessage error={error} />}
        </TableContainer>
        <Grid container style={{ backgroundColor: "#fafafa" }}>
          <Grid container item xs={4} alignContent="center">
            <div style={{ marginLeft: "1rem" }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            </div>
          </Grid>
          <Grid container item xs={8}>
            {total && total > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                rowsPerPage={rowsPerPage}
                page={page}
                count={total}
                component="div"
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default MatchingLoaderModal;
